import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_BASE;

// ── JWT expiry helpers ──────────────────────────────────────────────────────────
function tokenExpiryMs(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : 0; // exp is in seconds
  } catch {
    return 0;
  }
}
function isExpired(token) {
  const exp = tokenExpiryMs(token);
  return exp > 0 && Date.now() >= exp;
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // central place to drop a dead session and send the user to login
  const expireSession = useCallback(() => {
    localStorage.removeItem('bsc_token');
    localStorage.removeItem('bsc_user');
    delete axios.defaults.headers.common['Authorization'];
    sessionStorage.setItem('bsc_session_expired', '1');
    setUser(null);
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  }, []);

  useEffect(() => {
    // restore session on load — but only if the token is still valid
    const token    = localStorage.getItem('bsc_token');
    const userData  = localStorage.getItem('bsc_user');
    if (token && userData && !isExpired(token)) {
      setUser(JSON.parse(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else if (token && isExpired(token)) {
      expireSession();
    }
    setLoading(false);

    // safety net: any 401 from a default-axios call -> expire session
    const interceptorId = axios.interceptors.response.use(
      r => r,
      err => {
        if (err.response?.status === 401) expireSession();
        return Promise.reject(err);
      }
    );

    // proactive: check token expiry every 60s so an idle tab logs out cleanly
    const timer = setInterval(() => {
      const t = localStorage.getItem('bsc_token');
      if (t && isExpired(t)) expireSession();
    }, 60000);

    return () => {
      axios.interceptors.response.eject(interceptorId);
      clearInterval(timer);
    };
  }, [expireSession]);

  async function login(username, password) {
    const res = await axios.post(`${API_BASE}/auth/login`, { username, password });
    const { token, role, fullName, username: uname } = res.data;

    const userData = { token, role, fullName, username: uname };
    localStorage.setItem('bsc_token', token);
    localStorage.setItem('bsc_user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    sessionStorage.removeItem('bsc_session_expired');
    setUser(userData);
    return userData;
  }

  function logout() {
    localStorage.removeItem('bsc_token');
    localStorage.removeItem('bsc_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }

  const isAdmin    = user?.role === 'ADMIN';
  const isOperator = user?.role === 'OPERATOR' || isAdmin;
  const isViewer   = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAdmin, isOperator, isViewer }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}